<template>
  <div class="gl-list-order">
    <div class="">
      <v-card loader-height="2" class="shadow-off" :loading="loading" :disabled="loading" data-testid="gl-list__card">
        <v-card-text class="">
          <div class="d-flex flex-column gap-6">
            <!-- Title -->
            <div class="d-flex align-center">
              <PageHeader 
                :title="$nova.labelsName('gllist_page', 'threshold_label', 'GL Layout configuration')"
                :loading="loading"
                @refresh="refreshData"
            />
            </div>
            <!-- Summary Card with shadow -->
            <v-card class="gl-summary-card pa-4" data-testid="gl-list__summary-card">
              <div class="d-flex align-center space-between">
                <div class="d-flex align-center gap-4">
                  <CountSection :count="allgls.length" class="gap-120"
                    :networkLabel="'Total'"
                    :countLabel="$nova.labelsName('access_management','commentary_count_label','Count')" />
                  <CountSection :count="filteredGLCount" class="gap-120"
                    :networkLabel="selectedGLTypeTab === 0 ? 'Profit & Loss' : 'Balance Sheet'"
                    :countLabel="$nova.labelsName('access_management','commentary_count_label','Count')" />
                </div>
                <v-spacer></v-spacer>
                <div class="d-flex align-center gap-2" v-if="pendingchange">
                 
                  <v-btn class="shadow-off" color="primary" @click="confirmSaveDialog = true" data-testid="gl-list__save-btn">
                    <v-icon left small>mdi-content-save</v-icon>
                    <span>Save Changes</span>
                  </v-btn>
                </div>
              </div>
              <!-- Information Chip -->
              <div class="info-chip-wrapper d-flex mt-4">
                <v-chip class="noTransform ru-info-chip info-chip-content gap-2 align-center">
                  <v-icon class="mr-1 flex-shrink-0">mdi-information-outline</v-icon>
                  <span>Below is the list of all the GL's in this application. You can define the order in which the GL's have to be displayed here.</span>
                </v-chip>
              </div>
            </v-card>

            <!-- GL Type Folder Tabs + Table (grouped together to avoid gap-6 separation) -->
            <div class="gl-tabs-table-container">
              <div class="gl-tabs-wrapper d-flex align-center gap-2">
                <div 
                  class="folder-tab" 
                  :class="{ active: selectedGLTypeTab === 0 }"
                  @click="selectedGLTypeTab = 0"
                  data-testid="gl-list__tab-pl"
                >
                  Profit & Loss
                </div>
                <div 
                  class="folder-tab" 
                  :class="{ active: selectedGLTypeTab === 1 }"
                  @click="selectedGLTypeTab = 1"
                  data-testid="gl-list__tab-bs"
                >
                  Balance Sheet
                </div>
                <v-spacer></v-spacer>
              </div>

              <!-- Table Card with folder tab connection -->
              <v-card class="gl-table-card" id="gltable">
              <v-card-text class="pa-3">
              <lb-datatablenew
                v-if="visibleItems.length > 0"
                ref="glDataTable"
                data-testid="gl-list-table"
                :headers="tableHeaders"
                :items="visibleItems"
                :enableslot="['sno', 'code_description', 'counts']"
                hide-default-footer
                :items-per-page="-1"
                :loading="loading"
                fixed-header
                dense
                class="v-table-color"
              >
                <template v-slot:sno="{ item, index }">
                  <div class="d-flex align-center justify-center">
                    <span class="font-weight-medium" :class="{ 'order-changed': isOrderChanged(item) }">
                      {{ index + 1 }}
                    </span>
                    <v-tooltip bottom v-if="isOrderChanged(item)">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon 
                          x-small 
                          color="warning" 
                          class="ml-1 order-change-indicator"
                          v-bind="attrs"
                          v-on="on"
                        >
                          mdi-arrow-up-down
                        </v-icon>
                      </template>
                      <span>Moved {{ getOrderDirection(item) }} from position {{ getOriginalOrder(item) }}</span>
                    </v-tooltip>
                  </div>
                </template>

                <template v-slot:code_description="{ item }">
                  <div 
                    class="gl-code-cell-wrapper"
                    :class="{ 'gl-priority-active': item.priority === 1 }"
                    data-testid="gl-list__code-cell"
                  >
                    <div class="d-flex align-center justify-space-between gl-code-cell">
                      <div class="d-flex align-center gl-code-text" :class="{ 'font-weight-bold': item.priority === 1 }">
                        <span class="mr-2" :class="item.priority === 1 ? 'font-weight-bold' : 'font-weight-medium'">{{ item.code }}</span>
                        <span :class="item.priority === 1 ? 'font-weight-bold' : 'grey--text text--darken-1'">- {{ item.name }}</span>
                      </div>
                      <div class="d-flex align-center">
                        <v-tooltip v-if="item.sub_ledger_exists" bottom content-class="tooltip-bottom">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on" class="sl-drill-indicator d-inline-flex align-center ml-2">
                              <img src="/img/icons/DrillDown.svg" alt="Drill Down" class="sl-drill-icon" />
                            </span>
                          </template>
                          <span>Drill Down</span>
                        </v-tooltip>
                        <v-tooltip bottom content-class="tooltip-bottom">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              x-small
                              v-bind="attrs"
                              v-on="on"
                              class="ml-1 gl-star-btn"
                              :class="{ 'gl-star-btn--active': item.priority === 1 }"
                              @click.stop="togglePriority(item)"
                              data-testid="gl-list__priority-star-btn"
                            >
                              <v-icon small :color="item.priority === 1 ? 'primary' : ''">
                                {{ item.priority === 1 ? 'mdi-star' : 'mdi-star-outline' }}
                              </v-icon>
                            </v-btn>
                          </template>
                          <span>{{ item.priority === 1 ? 'Remove Priority' : 'Mark as Priority' }}</span>
                        </v-tooltip>
                        <v-tooltip v-if="isRootLevelItem(item)" bottom content-class="tooltip-bottom">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              x-small
                              v-bind="attrs"
                              v-on="on"
                              class="ml-1 gl-parent-toggle-btn"
                              :class="{ 'gl-parent-toggle-btn--na': item.parent === 'NA' }"
                              @click.stop="toggleParentGroup(item)"
                              data-testid="gl-list__parent-toggle-btn"
                            >
                              <v-icon small :color="item.parent === 'NA' ? 'primary' : 'grey'">
                                {{ item.parent === 'NA' ? 'mdi-folder-arrow-up' : 'mdi-folder-arrow-down' }}
                              </v-icon>
                            </v-btn>
                          </template>
                          <span>{{ item.parent === 'NA' ? 'Move to Primary' : 'Move to Ungrouped' }}</span>
                        </v-tooltip>
                      </div>
                    </div>
                  </div>
                </template>

                <template v-slot:counts="{ item }">
                  <div class="d-flex align-center justify-center">
                    <!-- OPTIMIZED: Single button that opens shared menu -->
                    <v-btn 
                      icon 
                      small 
                      :ref="'menuBtn_' + item.code"
                      @click.stop="openSharedMenu($event, item)"
                      data-testid="gl-list-move-ledger-btn"
                    >
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </div>
                </template>
              </lb-datatablenew>
              <!-- Loading State -->
              <div v-else-if="loading" class="d-flex flex-column align-center justify-center pa-10">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="48"
                  class="mb-4"
                ></v-progress-circular>
                <span class="text-body-1 grey--text">Fetching GL data...</span>
              </div>
              <!-- No Data State -->
              <div v-else class="title grey--text pa-5 text-center">
                No Data Found
              </div>
              </v-card-text>
            </v-card>
            </div>

          </div>

          <lb-dialog v-if="glgroupingdialog" v-model="glgroupingdialog" :loading="glgroupingloading" heading="Group GL" width="500">
            <template v-slot:body>
              <div>
                <div class="mb-4">
                  <div class="font-size-md mb-2">{{ glgroupingselected.code }} - {{ glgroupingselected.name }}</div>
                  <div class="">Select whether this GL should become the Parent(primary) or it should form part of an
                    existing parent GL</div>
                </div>
                <lb-dropdown hidedetails label="Select Parent" v-model="selectedparent"
                  :items="getParentListForChange()" itemtext="text" itemvalue="value" data-testid="gl-list__parent-dropdown" />
              </div>
            </template>
            <template v-slot:actions>
              <v-spacer></v-spacer>
              <v-btn v-if="selectedparent !== glgroupingselected.parent" @click="changeParent()" color="success"
                class="ml-2" data-testid="gl-list__change-parent-btn"><span>Change</span></v-btn>
            </template>
          </lb-dialog>

          <!-- OPTIMIZED: Single shared menu for all rows - positioned dynamically -->
          <v-menu
            v-model="sharedMenuOpen"
            :position-x="menuPosition.x"
            :position-y="menuPosition.y"
            absolute
            left
            :close-on-content-click="false"
            :nudge-width="320"
            transition="scale-transition"
          >
            <v-card v-if="sharedMenuOpen && activeMenuItem" width="420" class="rounded-lg elevation-2" data-testid="gl-list__shared-menu">
              <v-card-title class="pa-4 d-flex flex-column align-start">
                <span class="text-subtitle-1 font-weight-bold">Move Ledger</span>
                <div class="d-flex align-center full-width-max">
                  <span class="text-caption grey--text text-truncate mr-2">
                    {{ activeMenuItem.code }} - {{ activeMenuItem.name }}
                  </span>
                  <v-chip small
                    :color="activeMenuItem.reporting_type === 'bs' ? 'info' : 'success'" 
                    class="white--text font-weight-medium"
                  >
                    {{ activeMenuItem.reporting_type === 'bs' ? 'BS' : 'PL' }}
                  </v-chip>
                </div>
              </v-card-title>
              <v-divider></v-divider>

              <v-card-text class="pa-4">
                <div class="mb-4">
                  <v-autocomplete
                    v-model="selectedTargetLedger"
                    :items="currentSiblings"
                    placeholder="Search Ledger"
                    dense
                    outlined
                    clearable
                    hide-details
                    class="rounded"
                    item-text="name"
                    item-value="code"
                    return-object
                    :filter="filterLedger"
                    :menu-props="{ maxHeight: 250, eager: false, maxWidth: 380, minWidth: 380, overflowY: true }"
                    auto-select-first
                    cache-items
                  >
                    <template v-slot:item="{ item: ledgerItem }">
                      <div class="d-flex align-center justify-space-between full-width">
                        <div class="text-truncate mr-2 max-width-280" :title="ledgerItem.code + ' - ' + ledgerItem.name">{{ ledgerItem.code }} - {{ ledgerItem.name }}</div>
                        <!-- <v-chip 
                          small 
                          :color="ledgerItem.reporting_type === 'bs' ? 'info' : 'success'" 
                          class="white--text font-weight-medium"
                        >
                          {{ ledgerItem.reporting_type === 'bs' ? 'BS' : 'PL' }}
                        </v-chip> -->
                      </div>
                    </template>
                    <template v-slot:selection="{ item: ledgerItem }">
                      <div class="d-flex align-center">
                        <div class="text-truncate mr-2">{{ ledgerItem.code }} - {{ ledgerItem.name }}</div>
                        <!-- <v-chip 
                          small 
                          :color="ledgerItem.reporting_type === 'bs' ? 'info' : 'success'" 
                          class="white--text font-weight-medium"
                        >
                          {{ ledgerItem.reporting_type === 'bs' ? 'BS' : 'PL' }}
                        </v-chip> -->
                      </div>
                    </template>
                  </v-autocomplete>
                </div>

                <div class="mb-6">
                   <v-row dense>
                     <v-col cols="6" class="pr-2">
                       <v-btn 
                         block
                         outlined 
                         :color="movePosition === 'before' ? 'primary' : ''"
                         class="rounded text-capitalize"
                         @click="movePosition = 'before'"
                         :class="{'v-btn--active': movePosition === 'before'}"
                       >
                         Before
                         <v-icon right small v-if="movePosition === 'before'">mdi-chevron-up</v-icon>
                       </v-btn>
                     </v-col>
                     <v-col cols="6" class="pl-2">
                        <v-btn 
                         block 
                         outlined
                         :color="movePosition === 'after' ? 'primary' : ''"
                         class="rounded text-capitalize"
                         @click="movePosition = 'after'"
                         :class="{'v-btn--active': movePosition === 'after'}"
                       >
                         After
                         <v-icon right small v-if="movePosition === 'after'">mdi-chevron-down</v-icon>
                       </v-btn>
                     </v-col>
                   </v-row>
                </div>

                <v-btn
                  block
                  color="primary"
                  class="elevation-2 rounded"
                  @click="applyMoveLedger(activeMenuItem)"
                >
                  Apply
                </v-btn>
              </v-card-text>
            </v-card>
          </v-menu>

          <!-- OPTIMIZED: Confirmation dialog using lb-dialog for design consistency -->
          <lb-dialog 
            v-model="confirmSaveDialog" 
            heading="Confirm Layout Change" 
            :width="allChanges.length > 0 ? '700' : '500'"
            data-testid="gl-list__confirm-save-dialog"
          >
            <template v-slot:body>
              <div class="confirm-dialog-content d-flex align-start gap-3" data-testid="gl-list__confirm-save-content">
                <img src="/img/icons/AlertIcon.svg" alt="Warning Icon" class="" />
                <div class="confirm-dialog-text gap-2">
                  <h3 class="confirm-dialog-title">Warning</h3>
                  <p class="confirm-dialog-subtitle">
                    Changing the layout will update the GL page based on the changes you made.
                  </p>
                </div>
              </div>
              <!-- Changes Summary Table -->
              <div v-if="allChanges.length > 0" class="changes-summary-table mt-4" data-testid="gl-list__changes-summary">
                <v-simple-table dense class="changes-table">
                  <template v-slot:default>
                    <thead>
                      <tr class="changes-table__header">
                        <th class="text-left" style="width: 60px;">S.No</th>
                        <th class="text-left">GL Code & Description</th>
                        <th class="text-left" style="width: 180px;">Changes</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(change, idx) in allChanges" :key="change.code" class="changes-table__row">
                        <td>{{ idx + 1 }}</td>
                        <td>
                          <span class="font-weight-medium">{{ change.code }}</span>
                          <span class="grey--text text--darken-1"> - {{ change.name }}</span>
                        </td>
                        <td>
                          <div class="d-flex flex-column">
                            <span 
                              v-for="tag in change.tags" 
                              :key="tag.text" 
                              class="font-weight-bold"
                            >
                              {{ tag.text }}
                            </span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </template>
            <template v-slot:actions>
              <div class="d-flex justify-end gap-2">
                <v-btn text @click="confirmSaveDialog = false" class="secondaryBtn" data-testid="gl-list__confirm-save-cancel-button">Cancel</v-btn>
                <v-btn color="primary" @click="saveData()" data-testid="gl-list__confirm-save-continue-button">Continue</v-btn>
              </div>
            </template>
          </lb-dialog>
        </v-card-text>
      </v-card>

    </div>
  </div>
</template>

<script>
import CountSection from './GlSelection/CountSection.vue';
import PageHeader from '../components/PageHeader.vue';
import { mapActions } from 'vuex';
import helpers from '../utils/helpers';
import {
  createSnapshot,
  computeDelta,
  countPendingChanges,
  recalculateOrders,
  processTreeData,
  ROOT_PARENTS,
  UNGROUPED_GL_OFFSET,
} from '../utils/glListUtils';

export default {
  name: 'reportingunits',
  components: {
    CountSection,
    PageHeader
  },
  data: function () {
    return {
      loading: false,
      allgls: [],
      data: [],
      expandedgls: [],
      gllist: {},
      selectedgls: [],
      unapprovedrequest: [],
      glgroupingselected: {},
      glgroupingdialog: false,
      glgroupingloading: false,
      selectedparent: null,
      isNewList: false,
      getResult: [],
      expandedIds: [],
      confirmSaveDialog: false,
      selectedTargetLedger: null,
      movePosition: 'before',
      activeMenuGLCode: null,
      
      // ============================================================
      // OPTIMIZED: Delta tracking state
      // ============================================================
      originalSnapshot: null,      // Snapshot of original state for delta computation
      processedTreeData: [],       // Cached processed data for display
      dataVersion: 0,              // Version counter to trigger reactivity
      siblingsCache: {},           // Cache for siblings lookups
      currentSiblings: [],         // Pre-computed siblings for active menu
      pendingChangeCount: 0,       // Number of pending changes (computed)
      cachedVisibleItems: [],      // PERF: Cached visible items to avoid recomputation
      activeMenuItem: null,        // PERF: Currently active menu item (avoids lookup)
      sharedMenuOpen: false,       // PERF: Single shared menu open state
      menuPosition: { x: 0, y: 0 }, // PERF: Position for shared menu
      selectedGLTypeTab: 0,        // Tab index: 0 = Profit & Loss, 1 = Balance Sheet
      userChangedCodes: [],         // Tracks GLs explicitly changed by user (for display only)
    }
  },
  activated() {
    this.refreshData();
  },
  mounted() {
    this.refreshData();
  },
  
  /**
   * Watch for tab changes to refresh data with new reporting_type
   */
  watch: {
    selectedGLTypeTab() {
      // Refresh data when tab changes to load correct reporting_type
      this.refreshData();
    }
  },
  
  /**
   * IMPORTANT: Clean up state on component destroy
   */
  beforeDestroy() {
    this.clearState();
  },

  methods: {
    ...mapActions('varianceanalysis/gllist', ['saveGLOrderDelta']),
    
    /**
     * Clears component state on destroy
     */
    clearState() {
      this.getResult = [];
      this.processedTreeData = [];
      this.originalSnapshot = null;
      this.expandedIds = [];
      this.siblingsCache = {};
      this.currentSiblings = [];
      this.pendingChangeCount = 0;
      this.sharedMenuOpen = false;
      this.menuPosition = { x: 0, y: 0 };
      this.activeMenuGLCode = null;
      this.activeMenuItem = null;
      this.cachedVisibleItems = [];
      this.userChangedCodes = [];
    },
    
    /**
     * Check if item is the last child among its siblings at the same level
     */
    isLastChildAtLevel(item, itemIndex) {
      if (!item || item.level === 0) return false;
      
      const items = this.visibleItems;
      const currentLevel = item.level;
      
      // Look forward to find if there's another sibling at the same level
      for (let i = itemIndex + 1; i < items.length; i++) {
        const nextItem = items[i];
        if (!nextItem) continue;
        
        // If we hit an item at a lower level (parent level), we're the last child
        if (nextItem.level < currentLevel) {
          return true;
        }
        // If we hit an item at the same level, we're not the last child
        if (nextItem.level === currentLevel) {
          return false;
        }
        // If we hit an item at a higher level (child of current), continue looking
      }
      
      // If we reached the end, we're the last child
      return true;
    },
    /**
     * Get the tree connector lines that should be shown for this item
     */
    getTreeConnectors(item, itemIndex) {
      if (!item || item.level === 0) return [];
      
      const connectors = [];
      const items = this.visibleItems;
      const isInverted = false; // Standard tree
      
      // For each ancestor level, determine if we need a vertical line
      for (let level = 1; level <= item.level; level++) {
        let hasMoreSiblingsAtLevel = false;
        
        // Check forward to see if there are more siblings at this level
        for (let i = itemIndex + 1; i < items.length; i++) {
          const futureItem = items[i];
          if (!futureItem) continue;
          
          // If we hit an item at a lower level (parent level), stop looking
          if (futureItem.level < level) {
            break;
          }
          // If we hit an item at the same level, we need a vertical line to connect to it
          if (futureItem.level === level) {
            hasMoreSiblingsAtLevel = true;
            break;
          }
        }
        
        const isLastChild = level === item.level && this.isLastChildAtLevel(item, itemIndex);
        const isFirstChild = false;
        
        connectors.push({
          level: level,
          showVerticalLine: hasMoreSiblingsAtLevel,
          isCurrentLevel: level === item.level,
          isLastChild: isLastChild,
          isFirstChild: isFirstChild,
          isInverted: isInverted
        });
      }
      
      return connectors;
    },
    
    /**
     * OPTIMIZED: Recalculates order using utility function
     * Uses DFS traversal for hierarchical ordering
     */
    updateOrder() {
      this.getResult = recalculateOrders(this.getResult);
    },
    
    /**
     * OPTIMIZED: Process tree data using utility function
     * Separates data processing from rendering
     */
    processTreeData() {
      const { processedData, expandedIds } = processTreeData(this.getResult);
      
      // Always ensure all root items are in expandedIds
      // Merge new root items with existing expanded items
      const existingExpanded = new Set(this.expandedIds);
      for (const id of expandedIds) {
        existingExpanded.add(id);
      }
      this.expandedIds = Array.from(existingExpanded);
      
      this.processedTreeData = processedData;
      this.dataVersion++;
      
      // PERF: Pre-compute visible items immediately
      this.updateCachedVisibleItems();
      
      // Update pending change count
      this.updatePendingChangeCount();
    },
    
    /**
     * PERF: Updates the cached visible items array
     * Called only when data or expansion changes
     */
    updateCachedVisibleItems() {
      const expandedSet = new Set(this.expandedIds);
      this.cachedVisibleItems = this.processedTreeData.filter((item) => expandedSet.has(item.code));
    },
    
    /**
     * OPTIMIZED: Updates the pending change count
     * Uses delta tracking utility
     */
    updatePendingChangeCount() {
      if (this.originalSnapshot) {
        this.pendingChangeCount = countPendingChanges(this.getResult, this.originalSnapshot);
      } else {
        this.pendingChangeCount = 0;
      }
    },
    
    toggleExpand(item) {
      if (!this.hasChildren(item)) return;
      let childarray = item.children;
      let subchild = item.subsequent_children;
      if (this.expandedIds.includes(childarray[0])) {
        this.expandedIds = this.expandedIds.filter(
          (id) => !subchild.includes(id)
        );
      } else {
        this.expandedIds.push(...childarray);
      }
      // PERF: Update cached visible items after expansion change
      this.updateCachedVisibleItems();
    },
    hasChildren(item) {
      return item?.children?.length;
    },
    isExpanded(item) {
      let childarray = item.children;
      return this.expandedIds.includes(childarray[0]);
    },
    async refreshData() {
      return this.getData();
    },
    
    /**
     * Discard local changes and revert to original state without network call
     */
    discardChanges() {
      if (!this.originalSnapshot) return;
      
      // Revert getResult to match originalSnapshot
      // We iterate through getResult and restore the original order and priority for each item
      this.getResult.forEach(item => {
        const original = this.originalSnapshot.get(item.code);
        if (original) {
          item.order = original.order;
          item.priority = original.priority || 0;
        }
      });
      
      // Sort getResult by the restored order to ensure correct sequence
      this.getResult.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      // Re-process the tree data to update UI
      this.processTreeData();
      
      // Reset pending changes count
      this.pendingChangeCount = 0;
      this.userChangedCodes = [];
      
      this.$store.commit("sbInfo", "Changes discarded");
    },
    
    /**
     * OPTIMIZED: Fetches GL data and creates snapshot for delta tracking
     * Uses new v2/list API with reporting_type filter and pre-sorted response
     * @returns {Promise} - Resolves when data is fetched and processed
     */
    getData() {
      this.loading = true;
      
      // Use the new v2/list API with reporting_type filter
      return helpers.apiGet.call(this, "/v2/varianceanalysis/gllist/v2/list", {
        reporting_type: this.selectedGLType // 'pl' or 'bs' based on active tab
      })
        .then((response) => {
          // Data comes pre-sorted by order and includes sno from backend
          const data = response.data?.data || response.data || [];
          this.data = data;
          this.allgls = data;
          this.getResult = [...data];
          this.expandedIds = []; // Reset for fresh processing
          this.siblingsCache = {}; // Clear siblings cache
          
          this.updateOrder();
          this.processTreeData();
          
          // ============================================================
          // OPTIMIZED: Create snapshot AFTER processing for delta tracking
          // ============================================================
          this.originalSnapshot = createSnapshot(this.getResult);
          this.pendingChangeCount = 0;
          this.userChangedCodes = [];
        })
        .catch((err) => {
          console.error('[GLList]: Error fetching GL list', err);
          this.$store.commit('sbError', err?.response?.data?.message || err?.message || 'Failed to fetch GL list');
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * OPTIMIZED: Save data using delta-based approach
     * Only sends modified records to server
     */
    async saveData() {
      this.loading = true;
      this.confirmSaveDialog = false;

      try {
        // ============================================================
        // OPTIMIZED: Compute delta - only get changed records
        // ============================================================
        const changes = computeDelta(this.getResult, this.originalSnapshot);
        
        console.log('[GLList]: Saving delta changes', { 
          totalRecords: this.getResult.length, 
          changedRecords: changes.length 
        });
        
        // Early return if no actual changes
        if (changes.length === 0) {
          this.$store.commit("sbInfo", "No changes detected");
          this.loading = false;
          return;
        }
        
        // Call the delta-based API
        const response = await this.axios.post(
          "/v2/varianceanalysis/gllist/v2/save-order-delta", 
          { changes }
        );
        
        if (response.data.status === "success") {
          this.$store.commit("sbSuccess", `GL layout saved`);
          
          // ============================================================
          // RESET: Update snapshot to match current state immediately
          // This resets all order change indicators before refreshData
          // ============================================================
          this.originalSnapshot = createSnapshot(this.getResult);
          this.pendingChangeCount = 0;
          this.userChangedCodes = [];
          
          // Refresh data to get the latest state from server
          // Note: getData() handles its own loading state
          await this.refreshData();
        } else {
          throw new Error(response.data.message || "Error updating GL order");
        }
      } catch (err) {
        console.error('[GLList]: Error saving GL order', err);
        this.$store.commit("sbError", err?.response?.data?.message || err?.message || "Unknown error!");
        this.loading = false;
      }
    },
    
    interchangeposition(k, type, action) {
      if (action === 'down') {
        if (type === 'primary') {
          if (k < ((this.gllist['primary'] || []).length - 1)) {
            let temp = this.gllist['primary'][k];
            this.gllist['primary'][k] = this.gllist['primary'][k + 1];
            this.gllist['primary'][k + 1] = temp;
          }
        } else {
          if (k < ((this.gllist[type] || []).length - 1)) {
            let temp = this.gllist[type][k];
            this.gllist[type][k] = this.gllist[type][k + 1];
            this.gllist[type][k + 1] = temp;
          }
        }
      }
      else if (action === 'up') {
        if (type === 'primary') {
          if (k > 0) {
            let temp = this.gllist['primary'][k];
            this.gllist['primary'][k] = this.gllist['primary'][k - 1];
            this.gllist['primary'][k - 1] = temp;
          }
        } else {
          if (k > 0) {
            let temp = this.gllist[type][k];
            this.gllist[type][k] = this.gllist[type][k - 1];
            this.gllist[type][k - 1] = temp;
          }
        }
      }
      
      // Trigger change detection
      this.updatePendingChangeCount();
      let t = this.gllist;
      this.gllist = {};
      this.gllist = t;
    },
    selectAll() {
      this.selectedgls = this.allgls;
    },
    unSelectAll() {
      this.selectedgls = [];
    },
    expandColapseList(v) {
      if (this.expandedgls.indexOf(v) > -1) this.expandedgls.splice(this.expandedgls.indexOf(v), 1)
      else this.expandedgls.push(v);
    },
    /**
     * PERF OPTIMIZED: Opens the single shared menu at click position
     * Only ONE menu component in DOM instead of 4000
     */
    openSharedMenu(event, item) {
      // Get click position for menu placement
      const rect = event.target.getBoundingClientRect();
      this.menuPosition = {
        x: rect.right,
        y: rect.bottom + 4 // Small offset below the button
      };
      
      // Store active item reference directly - no lookup needed later
      this.activeMenuItem = item;
      this.activeMenuGLCode = item.code;
      this.selectedTargetLedger = null;
      this.movePosition = 'before';
      
      // PERF: Pre-compute siblings SYNCHRONOUSLY before menu opens
      this.currentSiblings = this.computeSiblingsSync(item);
      
      // Open the shared menu
      this.$nextTick(() => {
        this.sharedMenuOpen = true;
      });
    },
    
    /**
     * PERF OPTIMIZED: Synchronous sibling computation with caching
     * For root-level items (primary/NA), shows ALL root-level GLs of same reporting_type
     */
    computeSiblingsSync(item) {
      if (!item) return [];
      
      const isRootLevel = ROOT_PARENTS.has(item.parent);
      // For root-level items, use a shared cache key across primary/NA
      const cacheKey = isRootLevel ? `root_${item.reporting_type}` : `${item.parent}_${item.reporting_type}`;
      
      if (this.siblingsCache[cacheKey] && this.siblingsCache._version === this.dataVersion) {
        // Return cached siblings, excluding current item
        return this.siblingsCache[cacheKey].filter(n => n.code !== item.code);
      }
      
      // Build cache for this group
      const siblings = [];
      const len = this.getResult.length;
      for (let i = 0; i < len; i++) {
        const node = this.getResult[i];
        if (isRootLevel) {
          // Root-level: include all root-level GLs of same reporting_type
          if (ROOT_PARENTS.has(node.parent) && node.reporting_type === item.reporting_type) {
            siblings.push(node);
          }
        } else {
          // Child GL: same parent only
          if (node.parent === item.parent && node.reporting_type === item.reporting_type) {
            siblings.push(node);
          }
        }
      }
      
      // Sort by order
      siblings.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      // Cache it
      this.siblingsCache[cacheKey] = siblings;
      this.siblingsCache._version = this.dataVersion;
      
      // Return without current item
      return siblings.filter(n => n.code !== item.code);
    },
    
    handleMenuInput(isOpen) {
      if (!isOpen) {
        this.activeMenuGLCode = null;
        this.activeMenuItem = null;
        this.currentSiblings = [];
        this.sharedMenuOpen = false;
      }
    },
    resetMoveMenu() {
      this.selectedTargetLedger = null;
      this.movePosition = 'before';
    },
    filterLedger(item, queryText) {
      // Custom filter to search both code and name
      const search = (queryText || '').toLowerCase();
      const code = (item.code || '').toLowerCase();
      const name = (item.name || '').toLowerCase();
      return code.includes(search) || name.includes(search);
    },
    
    /**
     * Checks if a specific item's order has changed from original
     * Returns the original order if changed, null otherwise
     * @param {Object} item - The GL item to check
     * @returns {number|null} Original order if changed, null if unchanged
     */
    getOriginalOrder(item) {
      if (!this.originalSnapshot || !item) return null;
      const original = this.originalSnapshot.get(item.code);
      if (!original) return null;
      return original.order !== item.order ? original.order : null;
    },
    
    /**
     * Checks if item order has changed
     * @param {Object} item - The GL item to check
     * @returns {boolean} True if order changed
     */
    isOrderChanged(item) {
      return this.getOriginalOrder(item) !== null;
    },

    /**
     * Returns the direction the item moved ('up' or 'down')
     * @param {Object} item - The GL item to check
     * @returns {string} 'up' if moved to lower order number, 'down' if moved to higher order number
     */
    getOrderDirection(item) {
      const originalOrder = this.getOriginalOrder(item);
      if (originalOrder === null) return null;
      // Lower order number = higher in the list = moved up
      return item.order < originalOrder ? 'up' : 'down';
    },
    
    /**
     * OPTIMIZED: Apply move ledger with delta tracking
     * Uses cached activeMenuItem to avoid expensive lookups
     */
    applyMoveLedger(item) {
      if (!this.selectedTargetLedger) {
        this.$store.commit("sbError", "Please select a target GL to move relative to");
        return;
      }

      // PERF: Use activeMenuItem if available, otherwise fallback to lookup
      const sourceItem = this.activeMenuItem || item;
      const sourceItemIndex = this.getResult.findIndex(x => x.code === sourceItem.code);
      const targetItem = this.selectedTargetLedger; // Already have the object
      const targetItemIndex = this.getResult.findIndex(x => x.code === targetItem.code);

      if (sourceItemIndex === -1 || targetItemIndex === -1 || !targetItem) {
        this.$store.commit("sbError", "Error locating GL items");
        return;
      }

      // Rules check:
      // For root-level items, allow reordering across primary/NA without changing parent
      // For child items (parent is a GL code), block cross-parent moves
      const sourceIsRoot = ROOT_PARENTS.has(sourceItem.parent);
      const targetIsRoot = ROOT_PARENTS.has(targetItem.parent);
      
      if (sourceItem.parent !== targetItem.parent) {
        if (!sourceIsRoot || !targetIsRoot) {
          this.$store.commit("sbError", "Cannot move to a different parent");
          return;
        }
        // Root-level cross-parent: reorder only, parent stays unchanged
        // Parent toggle is handled separately via toggleParentGroup()
      }

      // Get all root-level siblings (same reporting_type) sorted by order
      const siblings = this.getResult
        .filter(n => {
          if (sourceIsRoot) {
            return ROOT_PARENTS.has(n.parent) && n.reporting_type === sourceItem.reporting_type;
          }
          return n.parent === sourceItem.parent && n.reporting_type === sourceItem.reporting_type;
        })
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      
      // Find positions in sorted siblings array
      const sourcePos = siblings.findIndex(s => s.code === sourceItem.code);
      const targetPos = siblings.findIndex(s => s.code === targetItem.code);
      
      if (sourcePos === -1 || targetPos === -1) {
        this.$store.commit("sbError", "Error finding sibling positions");
        return;
      }

      // Remove source from its current position
      const [movedItem] = siblings.splice(sourcePos, 1);
      
      // Calculate new position
      let newPos = targetPos;
      if (this.movePosition === 'after') {
        // If moving after, and source was before target, target position shifts down by 1
        newPos = sourcePos < targetPos ? targetPos : targetPos + 1;
      } else {
        // If moving before, and source was before target, target position shifts down by 1
        newPos = sourcePos < targetPos ? targetPos - 1 : targetPos;
      }
      
      // Insert at new position
      siblings.splice(newPos, 0, movedItem);
      
      // Reassign orders sequentially based on new positions
      // Find the base order (minimum order among siblings)
      const minOrder = Math.min(...siblings.map(s => s.order || 1));
      const baseOrder = minOrder > 0 ? minOrder : 1;
      
      siblings.forEach((sibling, idx) => {
        // Find the actual item in getResult and update its order
        const resultIdx = this.getResult.findIndex(r => r.code === sibling.code);
        if (resultIdx !== -1) {
          this.getResult[resultIdx].order = baseOrder + idx;
        }
      });
      
      this.processTreeData(); // Reprocess after order change
      
      // Invalidate siblings cache (order changed)
      this.siblingsCache = {};
      
      // Track this as a user-explicit change
      if (!this.userChangedCodes.includes(sourceItem.code)) {
        this.userChangedCodes.push(sourceItem.code);
      }
      
      // Close the shared menu
      this.sharedMenuOpen = false;
      this.activeMenuGLCode = null;
      this.activeMenuItem = null;
    },
    reorder(dt, key) {
      return dt.sort(function (a, b) { return a[key] - b[key]; });
    },
    
    /**
     * Checks if a GL item is root-level (parent is primary/NA/null/empty)
     * @param {Object} item - The GL item to check
     * @returns {boolean} True if root-level
     */
    isRootLevelItem(item) {
      return item && ROOT_PARENTS.has(item.parent);
    },

    /**
     * Toggles a GL item between parent: 'primary' and parent: 'NA'
     * Only for root-level items
     * @param {Object} item - The GL item from visibleItems
     */
    toggleParentGroup(item) {
      const resultItem = this.getResult.find(r => r.code === item.code);
      if (!resultItem) return;

      // Only allow toggle for root-level items
      if (!ROOT_PARENTS.has(resultItem.parent)) return;

      // Flip parent and adjust order offset
      if (resultItem.parent === 'NA') {
        resultItem.parent = 'primary';
        // Remove the visual offset that processTreeData added for NA items
        if (resultItem.order >= UNGROUPED_GL_OFFSET) {
          resultItem.order = resultItem.order - UNGROUPED_GL_OFFSET;
        }
      } else {
        resultItem.parent = 'NA';
      }

      // Track this as a user-explicit change
      if (!this.userChangedCodes.includes(item.code)) {
        this.userChangedCodes.push(item.code);
      }

      // Invalidate siblings cache since parent changed
      this.siblingsCache = {};

      // Re-process tree to update UI (handles order boost/unboost for NA)
      this.processTreeData();
    },

    /**
     * Toggles the priority flag on a GL item
     * 0 -> 1 (mark as priority), 1 -> 0 (unmark)
     * @param {Object} item - The GL item from visibleItems
     */
    togglePriority(item) {
      // Find the item in getResult (source of truth) and toggle
      const resultItem = this.getResult.find(r => r.code === item.code);
      if (!resultItem) return;
      
      const newPriority = resultItem.priority === 1 ? 0 : 1;
      resultItem.priority = newPriority;
      
      // Track this as a user-explicit change
      if (!this.userChangedCodes.includes(item.code)) {
        this.userChangedCodes.push(item.code);
      }
      
      // Re-process tree to propagate change to processedTreeData → cachedVisibleItems
      this.processTreeData();
    },
    
    /**
     * Checks if a GL item's priority has changed from original
     * @param {Object} item - The GL item to check
     * @returns {boolean} True if priority changed
     */
    isPriorityChanged(item) {
      if (!this.originalSnapshot || !item) return false;
      const original = this.originalSnapshot.get(item.code);
      if (!original) return false;
      return (item.priority || 0) !== (original.priority || 0);
    },
  },
  computed: {
    /**
     * OPTIMIZED: Computed property for pending change detection
     * Uses delta tracking instead of full comparison
     */
    pendingchange() {
      return this.pendingChangeCount > 0;
    },
    
    /**
     * Computes all changes (order + priority) for the confirmation dialog table
     * Returns array of { code, name, tags: [{ text, color }] }
     */
    allChanges() {
      if (!this.originalSnapshot) return [];
      
      const changes = [];
      
      for (const gl of this.getResult) {
        const original = this.originalSnapshot.get(gl.code);
        if (!original) continue;
        
        // Only show GLs the user explicitly acted on
        if (!this.userChangedCodes.includes(gl.code)) continue;
        
        const tags = [];
        const orderChanged = gl.order !== original.order;
        const parentChanged = gl.parent !== original.parent;
        const priorityChanged = (gl.priority || 0) !== (original.priority || 0);
        
        if (parentChanged) {
          tags.push({ text: gl.parent === 'primary' ? 'Moved to Primary' : 'Moved to Ungrouped' });
        }
        if (orderChanged) {
          tags.push({ text: 'Order Changed' });
        }
        
        if (priorityChanged) {
          if ((gl.priority || 0) === 1) {
            tags.push({ text: 'GL Marked' });
          } else {
            tags.push({ text: 'GL Unmarked' });
          }
        }
        
        if (tags.length > 0) {
          changes.push({
            code: gl.code,
            name: gl.name,
            tags,
          });
        }
      }
      
      return changes;
    },
    
    tableHeaders() {
      return [
        { text: 'S.No', value: 'sno', sortable: false, width: '80px' },
        { text: 'GL Code & Description', value: 'code_description', sortable: false },
        { text: 'Actions', value: 'counts', sortable: false, width: '80px', align: 'center' }
      ];
    },
    /**
     * Returns the GL type filter based on selected tab
     * 0 = Profit & Loss (pl), 1 = Balance Sheet (bs)
     */
    selectedGLType() {
      return this.selectedGLTypeTab === 0 ? 'pl' : 'bs';
    },
    /**
     * Returns the count of GLs for the selected tab
     */
    filteredGLCount() {
      const hasReportingType = this.allgls.some(item => item.reporting_type);
      if (!hasReportingType) {
        return this.allgls.length;
      }
      return this.allgls.filter(item => item.reporting_type === this.selectedGLType).length;
    },
    visibleItems() {
      // Filter by selected GL type tab
      // If reporting_type is not set on items, show all items (backwards compatibility)
      const hasReportingType = this.cachedVisibleItems.some(item => item.reporting_type);
      if (!hasReportingType) {
        return this.cachedVisibleItems;
      }
      return this.cachedVisibleItems.filter(item => item.reporting_type === this.selectedGLType);
    },
    getParentList() {
      const dt = [...(this.gllist['primary'] || [])];
      dt.push({ code: "NA", name: "Ungrouped: Other sub-schedules and further Analysis Accounts" });
      return dt;
    },
  }

}
</script>

<style scoped>
/* Confirmation Dialog Styles */
::v-deep .confirm-dialog-content {
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
  padding: 0 var(--space-4);
}

::v-deep .confirm-dialog-icon {
  margin-right: var(--space-4);
  flex-shrink: 0;
  width: 88px;
  height: 88px;
}

::v-deep .confirm-dialog-text {
  display: flex;
  flex-direction: column;
}

::v-deep .confirm-dialog-title {
  font-size: var(--font-size-lg);
  font-weight: 600;
  margin-bottom: var(--space-2);
  color: var(--color-text-primary);
}

::v-deep .confirm-dialog-subtitle {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin-bottom: 0;
  line-height: 1.5;
}

/* Info Chip Styles */
.info-chip-wrapper {
  max-width: 100%;
  overflow: hidden;
}

.info-chip-content {
  white-space: normal;
}

transition-list * {
  transition: all 0.5s ease-in-out;
}

.node-container {
  height: 50px;
  margin-right: 5px;
}
.node-label {
  top: 0;
  bottom: 0;
  left: 8px;
  /* width: 1px; */
  /* background-color: #000000; */
  display: block;
  /* Important for visibility */
  height: 100%;
  /* Make sure it stretches */
  content: "";
}

.childlevele-0 {
  width: 0.5px;
  background-color: #383838;
}
.childlevele-1 {
  width: 0.5px;
  background-color: #505050;
}
.childlevele-2 {
  width: 0.5px;
  background-color: #696969;
}
.childlevele-3 {
  width: 0.5px;
  background-color: #808080;
}
.childlevele-4 {
  width: 0.5px;
  background-color: #989898;
}
.childlevele-5 {
  width: 0.5px;
  background-color: #b8b8b8;
}
.childlevele-6 {
  width: 0.5px;
  background-color: #c0c0c0;
}
.childlevele-7 {
  width: 0.5px;
  background-color: #d0d0d0;
}
.childlevele-8 {
  width: 0.5px;
  background-color: #888888;
}

/* Tree Cell Styles */
.tree-cell {
  position: relative;
  overflow: visible;
}

.tree-node-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  min-height: 42px;
}

.tree-connectors {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  height: 100%;
  width: 100%;
  pointer-events: none;
}

/* Tree hierarchy level colors */
.tree-level-1 .vertical-line,
.tree-level-1 .horizontal-branch,
.parent-child-line.tree-level-1 {
  background-color: var(--green-500);
}

.parent-child-line.tree-level-1 {
  left: var(--space-4-5) !important;
}

.tree-level-2 .vertical-line,
.tree-level-2 .horizontal-branch,
.parent-child-line.tree-level-2 {
  background-color: var(--red-500);
}

.tree-level-3 .vertical-line,
.tree-level-3 .horizontal-branch,
.parent-child-line.tree-level-3 {
  background-color: var(--blue-500);
}

.tree-level-4 .vertical-line,
.tree-level-4 .horizontal-branch,
.parent-child-line.tree-level-4 {
  background-color: var(--logo-color3, #6b67ce);
}

.tree-level-5 .vertical-line,
.tree-level-5 .horizontal-branch,
.parent-child-line.tree-level-5 {
  background-color: var(--green-500);
}

.tree-level-6 .vertical-line,
.tree-level-6 .horizontal-branch,
.parent-child-line.tree-level-6 {
  background-color: var(--red-500);
}

.tree-connector-segment {
  position: absolute;
  top: -5px;
  height: 120%;
  width: var(--space-9);
}

.vertical-line {
  position: absolute;
  left: -1px;
  top: 4px;
  height: 100%;
  width: var(--border-md);
}

.vertical-line.half-height {
  height: 58%;
  top: -4px;
}

.horizontal-branch {
  position: absolute;
  left: -1px;
  top: 50%;
  transform: translateY(-50%);
  width: var(--space-sm);
  height: 3px;
  background-color: var(--green-500);
}

.tree-expand-icon {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;
}

.expand-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: var(--space-3xs);
  background-color: var(--grey-white);
  position: relative;
}

.expand-icon .v-icon {
  color: var(--color-primary-base);
}

.parent-child-line {
  position: absolute;
  width: var(--border-md);
  left: var(--space-4-5);
  background-color: var(--color-primary-base);
  top: 50%;
  height: calc(50% + 21px);
}

/* Animations */
@keyframes rayTraceDown {
  0% { clip-path: inset(0 0 100% 0); }
  100% { clip-path: inset(0 0 0 0); }
}

@keyframes rayTraceRight {
  0% { clip-path: inset(0 100% 0 0); }
  100% { clip-path: inset(0 0 0 0); }
}

.parent-child-line {
  animation: rayTraceDown 0.6s ease-out 1 forwards;
  animation-fill-mode: forwards;
}

.tree-connector-segment .vertical-line {
  clip-path: inset(0 0 100% 0);
  animation: rayTraceDown 0.5s ease-out 1 forwards;
  animation-fill-mode: forwards;
}

.tree-connector-segment .horizontal-branch {
  clip-path: inset(0 100% 0 0);
  animation: rayTraceRight 0.4s ease-out 1 forwards;
  animation-fill-mode: forwards;
}

.tree-level-1 .vertical-line { animation-delay: 0.1s; }
.tree-level-1 .horizontal-branch { animation-delay: 0.3s; }
.tree-level-2 .vertical-line { animation-delay: 0.2s; }
.tree-level-2 .horizontal-branch { animation-delay: 0.4s; }
.tree-level-3 .vertical-line { animation-delay: 0.3s; }
.tree-level-3 .horizontal-branch { animation-delay: 0.5s; }
.tree-level-4 .vertical-line { animation-delay: 0.4s; }
.tree-level-4 .horizontal-branch { animation-delay: 0.6s; }

.no-children-spacer {
  display: inline-block;
  width: var(--icon-md);
  height: var(--icon-md);
}

.leaf-node-icon {
  color: var(--color-primary-base) !important;
}

/* Summary Card Styles */
.gl-summary-card {
  background-color: var(--grey-white);
  border-radius: var(--radius-8, 8px);
  box-shadow: var(--shadow-md);
}

/* GL Tabs Wrapper - remove margin to connect tabs to card */
.gl-tabs-wrapper {
  margin-top: var(--space-4);
  margin-bottom: 0;
}

/* Folder Tabs Styles */
.folder-tab {
  padding: var(--space-4) var(--space-4);
  background-color: var(--grey-100);
  border-radius: var(--radius-8) var(--radius-8) 0 0;
  cursor: pointer;
  font-family: 'Roboto', sans-serif;
  font-size: var(--font-size-sm);
  color: var(--grey-700);
  transition: background-color 0.25s ease,
              color 0.25s ease,
              box-shadow 0.25s ease;
  font-weight: 400;
  border: 1px solid var(--color-border-default);
  border-bottom: none;
  position: relative;
  top: 1px;
  z-index: 1;
}

.folder-tab:hover {
  background-color: var(--grey-50);
}

.folder-tab.active {
  background-color: var(--grey-white);
  color: var(--color-primary-base);
  font-weight: 600;
  z-index: 3;
  border-top: 1px solid var(--color-border-default);
  border-left: 1px solid var(--color-border-default);
  border-right: 1px solid var(--color-border-default);
  border-bottom: 1px solid var(--grey-white);
}

/* GL Table Card connected to folder tabs */
.gl-table-card {
  background-color: var(--grey-white);
  border-radius: var(--radius-8);
  border-top-left-radius: 0;
  border: 1px solid var(--color-border-default);
  box-shadow: var(--shadow-md);
  margin-top: -1px;
  position: relative;
  z-index: 2;
}

/* Order Change Indicator Styles */
.order-changed {
  color: var(--color-warning);
  font-weight: 600;
}

.order-change-indicator {
  animation: pulse-warning 1.5s ease-in-out infinite;
}

@keyframes pulse-warning {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Utility Classes */
.full-width {
  width: 100%;
}

.full-width-max {
  max-width: 100%;
}

.max-width-280 {
  max-width: 17.5rem;
}

.sl-drill-icon {
  width: var(--icon-md, 24px);
  height: var(--icon-md, 24px);
}

/* Ensure the slot wrapper divs from lb-datatablenew stretch full width 
   so justify-space-between can push the drill-down icon to the right */
.gl-list-order ::v-deep #gltable td > div {
  width: 100%;
}

/* ============================================================
   Priority Star / GL Code Cell Styles
   ============================================================ */

/* Wrapper for the GL code cell - provides hover & priority styling */
.gl-code-cell-wrapper {
  position: relative;
  padding: var(--space-1, 2px) var(--space-2, 4px);
  border: 2px solid transparent;
  border-radius: var(--radius-4, 4px);
  transition: border-color 0.25s ease,
              background-color 0.25s ease,
              box-shadow 0.25s ease;
}

/* Hover state - light darken + border animation */
.gl-code-cell-wrapper:hover {
  background-color: var(--grey-50, #fafafa);
  border-color: var(--grey-300, #e0e0e0);
}

/* Star button - hidden by default, shown on hover */
.gl-star-btn {
  opacity: 0;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.gl-code-cell-wrapper:hover .gl-star-btn {
  opacity: 1;
}

/* Star button active state - always visible when priority is 1 */
.gl-star-btn--active {
  opacity: 1 !important;
}

/* Active priority state for the cell */
.gl-priority-active {
  border-color: var(--color-primary-base, #1976d2);
  background-color: var(--grey-50, #fafafa);
}

.gl-priority-active:hover {
  border-color: var(--color-primary-base, #1976d2);
  background-color: var(--grey-100, #f5f5f5);
}

/* Description text color when priority is active */
.gl-priority-description {
  color: var(--text-grey-sub, var(--grey-500, #9e9e9e));
}

/* ============================================================
   Changes Summary Table Styles (Confirm Dialog)
   ============================================================ */

.changes-summary-table {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid var(--color-border-default, #e0e0e0);
  border-radius: var(--radius-4, 4px);
}

.changes-table__header th {
  background-color: var(--grey-100, #f5f5f5) !important;
  font-size: var(--font-size-xs, 12px) !important;
  color: var(--color-text-secondary, #757575) !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px;
}

.changes-table__row td {
  font-size: var(--font-size-sm, 14px) !important;
  padding-top: var(--space-2, 8px) !important;
  padding-bottom: var(--space-2, 8px) !important;
}

.change-tag {
  font-size: 10px !important;
  height: 20px !important;
}
</style>
